import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useForm } from "antd/es/form/Form";
import useSearch from "../../user-management/useSearch";
import { useTranslation } from "react-i18next";
import useNotification from "../../../../components/notification/notification";
import { isReferencedError } from "../../../../common/utils/referenced-error";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import useAdminEvents from "../../../../hooks/useAdminEvents";

const debounce = (fn, ms = 400) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

export const useAdminResource = (
  ApiResource,
  listName,
  notificationPath,
  urlName = null,
  itemName = null,
  basePath = "administration"
) => {
  const [form] = useForm();
  const { search, handleUpdateSearchValue } = useSearch();
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [selectedResource, setSelectedResource] = useState(undefined);
  const [resourceList, setResourceList] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [, setHasImage] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [isUpdatedImage, setIsUpdatedImage] = useState(false);
  const [factCitations, setFactCitations] = useState([]);
  const [factUrls, setFactUrls] = useState([]);
  const [filters, setFilters] = useState({});
  const [isLoading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [sorting, setSorting] = useState({
    sortField: "updatedAt",
    sortOrder: "DESC",
  });

  const navigate = useProjectNavigate();
  const location = useLocation();

  const listRef = useRef(resourceList);
  useEffect(() => {
    listRef.current = resourceList;
  }, [resourceList]);

  // ✅ request guard: кожен fetch має свій seq, і тільки останній має право писати в state
  const requestSeqRef = useRef(0);

  const mergeUniqueById = (prev = [], incoming = []) => {
    const seen = new Set(prev.map((i) => i.id));
    return prev.concat(incoming.filter((i) => !seen.has(i.id)));
  };

  const handleNavigate = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  /** ---------------- core pagination loader ---------------- */
  const loadPage = useCallback(
    async (nextPage, { replace = false, queryOverride, force = false } = {}) => {
      if (isLoading && !force) return;

      // ✅ request guard start
      const mySeq = ++requestSeqRef.current;

      setLoading(true);
      try {
        const params = {
          query: (queryOverride ?? search) || "",
          ...filters,
          ...sorting,
          page: nextPage,
        };

        const resp = await ApiResource.get(params);
        const data = resp?.data || {};

        // ✅ якщо це не останній запит — ігноруємо відповідь
        if (mySeq !== requestSeqRef.current) return;

        const incoming = Array.isArray(data[listName]) ? data[listName] : [];

        setHasMore(Boolean(data?.hasMore));
        setPage(Number.isFinite(data?.page) ? data.page : nextPage);

        if (nextPage === 1) {
          const sameOptions = (a = [], b = []) =>
            Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every(
              (x, i) =>
                x?.id === b[i]?.id &&
                (x?.name ?? x?.value) === (b[i]?.name ?? b[i]?.value)
            );

          if (Array.isArray(data?.authors))
            setAuthors((prev) => (sameOptions(prev, data.authors) ? prev : data.authors));
          if (Array.isArray(data?.tags))
            setTags((prev) => (sameOptions(prev, data.tags) ? prev : data.tags));
          if (Array.isArray(data?.categories))
            setCategories((prev) =>
              sameOptions(prev, data.categories) ? prev : data.categories
            );
          if (Array.isArray(data?.factCitations))
            setFactCitations((prev) =>
              sameOptions(prev, data.factCitations) ? prev : data.factCitations
            );
          if (Array.isArray(data?.factUrls))
            setFactUrls((prev) =>
              sameOptions(prev, data.factUrls) ? prev : data.factUrls
            );

          // fallback dictionaries
          if (!Array.isArray(data?.tags) && ApiResource.getTags) {
            try {
              const r = await ApiResource.getTags();
              // ✅ guard (у випадку якщо getTags довго)
              if (mySeq === requestSeqRef.current && r?.data?.tags) setTags(r.data.tags);
            } catch {}
          }

          if (!Array.isArray(data?.categories) && ApiResource.getCategories) {
            try {
              const r = await ApiResource.getCategories();
              // ✅ guard
              if (mySeq === requestSeqRef.current && r?.data?.categories)
                setCategories(r.data.categories);
            } catch {}
          }
        }

        setResourceList((prev) => (replace ? incoming : mergeUniqueById(prev, incoming)));

        setSelectedResource((prev) => {
          if (!prev) return prev;
          const pool = replace ? incoming : mergeUniqueById(listRef.current, incoming);
          return pool.find((x) => x.id === prev.id) || prev;
        });
      } catch (e) {
        // ✅ якщо це вже не останній запит — помилку можна мовчки ігнорувати
        if (mySeq !== requestSeqRef.current) return;
        console.log("Resource fetch error:", e);
      } finally {
        // ✅ тільки останній запит має право вимикати loading
        if (mySeq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [ApiResource, filters, isLoading, listName, search, sorting]
  );

  const fetchNext = useCallback(() => {
    if (!hasMore || isLoading) return;
    return loadPage(page + 1);
  }, [hasMore, isLoading, loadPage, page]);

  const resetAndFetch = useCallback(
    async (q) => {
      // Don't blank the list before reloading: loadPage(1, { replace: true })
      // swaps the rows in place, and the request-seq guard guarantees only the
      // latest fetch writes. Pre-clearing made every refetch (save → close →
      // server event) flash an empty list — the "parade" of re-renders. Page 1
      // replaces (never merges), so a stale listRef can't leak in here either.
      setPage(0);
      setHasMore(true);

      await loadPage(1, { replace: true, queryOverride: q, force: true });
    },
    [loadPage]
  );

  const debouncedSearch = useMemo(() => debounce((q) => resetAndFetch(q), 400), [
    resetAndFetch,
  ]);

  const onSearchChange = (val) => {
    handleUpdateSearchValue(val);
    if (val === "" || val == null) {
      resetAndFetch("");
    } else {
      debouncedSearch(val);
    }
  };

  /** ---------------- CRUD & helpers ---------------- */

  const updateResourceSilent = async (id, params) => {
    await ApiResource.update(id, params);
  };

  const createResource = async (params) => {
    setLoading(true);
    try {
      await ApiResource.create(params);
      setSelectedResource(null);
      form.resetFields();
      await resetAndFetch(search);
      openNotification({
        type: "success",
        message: (
          <p>
            {t(`${notificationPath}.created`, {
              title: listName === "facts" ? params.text : params.title || params.text,
            })}
          </p>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const open = (resource, mode = "show") => {
    setSelectedResource(resource);
    form.setFieldsValue({ ...resource, image: null, hasImage: false });
    if (mode === "edit") {
      handleNavigate(`/${basePath}/${urlName || listName}/${resource.id}/edit`);
    } else {
      handleNavigate(`/${basePath}/${urlName || listName}/${resource.id}`);
    }
  };

  const close = async () => {
    await form.resetFields();
    setSelectedResource(null);
    await resetAndFetch(search);
    updateIsUpdatedImage(false);
    handleNavigate(`/${basePath}/${urlName || listName}`);
  };

  const updateResource = async (id, params) => {
    setLoading(true);
    try {
      await ApiResource.update(id, params);
      await Promise.all([resetAndFetch(search), getTags(), getCategories()]);
    } finally {
      setLoading(false);
    }
  };

  const createAndOpen = async () => {
    setLoading(true);
    try {
      const response = await ApiResource.create();
      const newItem = response?.data?.[itemName];
      setSelectedResource(newItem);
      form.setFieldsValue({ ...newItem, image: null, hasImage: false });
      handleNavigate(`/${basePath}/${urlName || listName}/${newItem.id}/edit`);
      await resetAndFetch(search);
    } catch (error) {
      openNotification({
        type: "error",
        message: <p>{t(`${notificationPath}.create_failed`)}</p>,
      });
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    await form.resetFields();
    await form.setFieldValue("image", null);
    setSelectedResource({
      title: "Title",
      text: null,
      image: null,
      status: "draft",
    });
    handleNavigate(`/${basePath}/${urlName || listName}/create`);
  };

  const isNewResourceValid = (resource) => {
    let isValid = true;

    if (listName === "pages") {
      if (!resource.title || resource.title.trim() === "") {
        openNotification({
          type: "error",
          message: <p>{t(`${notificationPath}.no_title`)}</p>,
        });
        isValid = false;
      }
    }

    if (listName === "facts" && (!resource.text || resource.text.trim() === "")) {
      openNotification({
        type: "error",
        message: <p>{t(`${notificationPath}.no_title`)}</p>,
      });
      isValid = false;
    }

    return isValid;
  };

  const update = () => {
    const fields = form.getFieldsValue();
    const updatedSelectedResource = { ...selectedResource, ...fields };

    if (!isUpdatedImage) delete updatedSelectedResource.image;

    if (!isNewResourceValid(updatedSelectedResource)) return;

    setSelectedResource(updatedSelectedResource);

    return updatedSelectedResource.id
      ? updateResource(updatedSelectedResource.id, updatedSelectedResource)
      : createResource(updatedSelectedResource);
  };

  const remove = async (id, title) => {
    try {
      await ApiResource.remove(id);
      await resetAndFetch(search);
      openNotification({
        type: "success",
        message: <p>{t(`${notificationPath}.deleted`, { title })}</p>,
      });
    } catch (err) {
      if (isReferencedError(err)) {
        openNotification({
          type: "error",
          message: <p data-testid="resource-delete-referenced">{t("common.referenced_delete_error")}</p>,
        });
      } else {
        openNotification({
          type: "error",
          message: <p>{t(`${notificationPath}.delete_failed`)}</p>,
        });
      }
    }
  };

  const getTags = useCallback(async () => {
    const response = await ApiResource.getTags?.();
    if (response?.data?.tags) setTags(response.data.tags);
  }, [ApiResource]);

  const getCategories = useCallback(async () => {
    const response = await ApiResource.getCategories?.();
    if (response?.data?.categories) setCategories(response.data.categories);
  }, [ApiResource]);

  useAdminEvents({
    onTagChanged: ApiResource.getTags ? getTags : undefined,
    onCategoryChanged: ApiResource.getCategories ? getCategories : undefined,
    onFitFactChanged:
      listName === "facts"
        ? (payload) => {
            const isInlineUpdate = payload?.action === "updated" && payload?.fact;

            if (isInlineUpdate) {
              setResourceList((prev) =>
                (prev || []).map((item) =>
                  item.id === payload.fact.id ? { ...item, ...payload.fact } : item
                )
              );
            } else {
              resetAndFetch(search);
            }

            setSelectedResource((prev) => {
              if (!prev?.id) return prev;

              const changedIds = [
                ...(payload?.deletedIds || []),
                payload?.id,
                payload?.activeCopyId,
              ].filter(Boolean);

              if (!changedIds.includes(prev.id)) return prev;

              if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
                form.resetFields();
                handleNavigate(`/${basePath}/${urlName || listName}`);
                return null;
              }

              if (payload?.action === "published" && payload?.activeCopyId === prev.id) {
                return prev;
              }

              if (
                payload?.action === "discarded" &&
                payload?.deletedIds?.includes(prev.id) &&
                payload?.fact?.id
              ) {
                handleNavigate(`/${basePath}/${urlName || listName}/${payload.fact.id}`);
                return payload.fact;
              }

              return payload?.fact ? { ...prev, ...payload.fact } : prev;
            });
          }
        : undefined,
    onArticleChanged:
      listName === "pages"
        ? (payload) => {
            const isInlineUpdate = payload?.action === "updated" && payload?.page;

            if (isInlineUpdate) {
              setResourceList((prev) =>
                (prev || []).map((item) =>
                  item.id === payload.page.id ? { ...item, ...payload.page } : item
                )
              );
            } else {
              resetAndFetch(search);
            }

            setSelectedResource((prev) => {
              if (!prev?.id) return prev;

              const changedIds = [
                ...(payload?.deletedIds || []),
                payload?.id,
                payload?.activeCopyId,
              ].filter(Boolean);

              if (!changedIds.includes(prev.id)) return prev;

              if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
                form.resetFields();
                handleNavigate(`/${basePath}/${urlName || listName}`);
                return null;
              }

              if (payload?.action === "published" && payload?.activeCopyId === prev.id) {
                return prev;
              }

              return payload?.page ? { ...prev, ...payload.page } : prev;
            });
          }
        : undefined,
  });

  useEffect(() => {
    resetAndFetch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetAndFetch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sorting]);

  // const isFilterEmpty = (obj) =>
  //   !obj ||
  //   Object.keys(obj).length === 0 ||
  //   Object.values(obj).every(
  //     (v) =>
  //       (Array.isArray(v) && v.length === 0) ||
  //       (typeof v === "object" && v !== null && Object.keys(v).length === 0) ||
  //       v === undefined ||
  //       v === null
  //   );

  const updateSorting = useCallback((sortField, sortOrder) => {
    setSorting({ sortField, sortOrder });
  }, []);

  const normalizeFilters = (obj) => {
    const out = {};
    if (!obj || typeof obj !== "object") return out;

    Object.entries(obj).forEach(([k, v]) => {
      if (v == null) return;

      if (Array.isArray(v)) {
        const arr = v.filter((x) => x != null && `${x}`.trim() !== "");
        if (arr.length) out[k] = arr;
        return;
      }

      if (typeof v === "object") {
        const keys = Object.keys(v);
        if (keys.length) out[k] = v;
        return;
      }

      // boolean / number / string
      const s = `${v}`.trim();
      if (s !== "") out[k] = v;
    });

    return out;
  };

  const stableStringify = (o) => JSON.stringify(o); // достатньо, бо після normalize порядок стабільний

  const updateFilterValue = useCallback((newFilters) => {
    const next = normalizeFilters(newFilters);

    setFilters((prevFilters) => {
      const prev = normalizeFilters(prevFilters);

      if (stableStringify(prev) === stableStringify(next)) {
        return prevFilters; // нічого не міняємо
      }

      // ✅ важливо: повертаємо НОВИЙ об'єкт, щоб React точно побачив зміну
      return { ...next };
    });
  }, []);

  const updateHasImage = (value) => setHasImage(value);
  const updateIsUpdatedImage = (value) => setIsUpdatedImage(value);
  const isActive = selectedResource?.isActive;

  return {
    form,
    list: resourceList,
    active: selectedResource,
    update,
    open,
    close,
    create,
    createAndOpen,

    search,
    handleUpdateSearchValue: onSearchChange,

    // crud
    remove,
    updateResource,
    updateResourceSilent,

    tags,
    categories,
    authors,
    factCitations,
    factUrls,

    filters,
    isLoading,
    isUpdatedImage,
    isActive,

    updateFilterValue,
    updateHasImage,
    updateIsUpdatedImage,
    updateSorting,

    getResources: () => resetAndFetch(search),

    hasMore,
    fetchNext,
  };
};
